import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import SectionHeader from "@/components/feature/panel/SectionHeader";
import EmptyState from "@/components/feature/panel/EmptyState";
import PanelLinks from "@/components/feature/panel/PanelLinks";
import { SUBJECTS } from "@/hooks/useTeacherContent";
import {
  useAiVideoJobs,
  useAiVideoOptions,
  type AiVideoJob,
} from "@/hooks/useAiVideoLessons";
import { getDataClient } from "@/lib/dataClient";

const inputClass =
  "w-full h-10 px-3 rounded-md border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 bg-background-50";

interface FormState {
  title: string;
  subject: string;
  script: string;
  avatar_id: string;
  voice_id: string;
}

const STATUS_LABEL: Record<string, string> = {
  pending: "Sırada",
  processing: "Üretiliyor",
  completed: "Tamamlandı",
  failed: "Hata",
};

function statusBadge(status: string) {
  if (status === "completed") return "bg-green-50 text-green-700";
  if (status === "failed") return "bg-red-50 text-red-700";
  return "bg-accent-100 text-accent-800";
}

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" });
}

export default function AiVideoDers() {
  const { data: jobs, loading, error, reload, createJob, checkStatus } = useAiVideoJobs();
  const { options } = useAiVideoOptions();
  const list = jobs ?? [];

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>({
    title: "",
    subject: SUBJECTS[0],
    script: "",
    avatar_id: "",
    voice_id: "",
  });
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Üretimde olan işler varsa otomatik durum kontrolü (20 saniyede bir).
  const processingIds = list
    .filter((j) => j.status === "processing" || j.status === "pending")
    .map((j) => j.id)
    .join(",");

  useEffect(() => {
    if (!processingIds) return;
    const t = setInterval(async () => {
      try {
        const dataClient = getDataClient();
        await dataClient.functions.invoke("ai-video", { body: { action: "check" } });
      } catch {
        /* sessizce yut */
      }
      reload();
    }, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processingIds]);

  const openCreate = () => {
    setForm({
      title: "",
      subject: SUBJECTS[0],
      script: "",
      avatar_id: options.avatars[0]?.id ?? "",
      voice_id: options.voices[0]?.id ?? "",
    });
    setFormError(null);
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);
    if (!form.title.trim()) {
      setFormError("Lütfen bir başlık girin.");
      return;
    }
    if (!form.script.trim()) {
      setFormError("Lütfen senaryoyu (anlatım metnini) yazın.");
      return;
    }
    if (!form.avatar_id) {
      setFormError("Lütfen bir avatar seçin.");
      return;
    }
    setBusy(true);
    try {
      await createJob({
        title: form.title.trim(),
        subject: form.subject,
        script: form.script.trim(),
        avatar_id: form.avatar_id,
        voice_id: form.voice_id,
      });
      setShowForm(false);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Üretim başlatılırken bir hata oluştu.");
    } finally {
      setBusy(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await checkStatus();
    } catch {
      /* durum sorgulanamadı, sadece listeyi tazele */
      reload();
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <SectionHeader
          title="AI Video Ders"
          description="Senaryoyu yaz, avatar öğretmen senin yerine ders anlatsın. Üretilen video otomatik olarak Video Dersler'e eklenir."
        />
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-2 h-11 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-magic-line text-base" aria-hidden="true"></i>
          AI Video Oluştur
        </button>
      </div>

      {!options.configured && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
          <i className="ri-information-line text-amber-600 text-lg mt-0.5" aria-hidden="true"></i>
          <div>
            <p className="text-sm font-semibold text-amber-800">HeyGen API anahtarı henüz tanımlı değil</p>
            <p className="text-sm text-amber-700 mt-0.5">
              Video üretimi için yöneticinin dataClient'e <strong>HEYGEN_API_KEY</strong> eklemesi gerekiyor.
              Eklenene kadar arayüzü inceleyebilirsin ama üretim başlatılamaz.
            </p>
          </div>
        </div>
      )}

      {loading ? (
        <EmptyState icon="ri-loader-4-line" title="Yükleniyor..." />
      ) : error ? (
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 text-center">
          <p className="text-sm text-foreground-600">{error}</p>
          <button
            type="button"
            onClick={reload}
            className="mt-4 inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-md text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
          >
            <i className="ri-refresh-line" aria-hidden="true"></i>
            Tekrar dene
          </button>
        </div>
      ) : list.length === 0 ? (
        <EmptyState
          icon="ri-magic-line"
          title="Henüz AI video ders yok"
          description="İlk videonu üretmek için 'AI Video Oluştur' butonuna tıkla ve bir senaryo yaz."
        />
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-foreground-500">
              Toplam <strong className="text-foreground-800">{list.length}</strong> üretim
            </p>
            <button
              type="button"
              onClick={handleRefresh}
              disabled={refreshing}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-background-200 text-foreground-600 text-sm font-medium hover:bg-background-100 disabled:opacity-60 transition-colors whitespace-nowrap"
            >
              <i className={`ri-refresh-line ${refreshing ? "animate-spin" : ""}`} aria-hidden="true"></i>
              Durumu Güncelle
            </button>
          </div>

          <div className="grid grid-cols-1 gap-3">
            {list.map((j: AiVideoJob) => (
              <div
                key={j.id}
                className="rounded-xl border border-background-200 bg-background-50 p-5 flex flex-col sm:flex-row sm:items-center gap-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="font-heading font-semibold text-base text-foreground-950">{j.title}</h3>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold ${statusBadge(j.status)}`}>
                      {j.status === "processing" && <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>}
                      {STATUS_LABEL[j.status] || j.status}
                    </span>
                  </div>
                  <p className="text-xs text-foreground-500">{j.subject} · {formatDate(j.created_at)}</p>
                  {j.error_message && (
                    <p className="text-xs text-red-700 mt-1">{j.error_message}</p>
                  )}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {j.status === "completed" && j.video_url && (
                    <a
                      href={j.video_url}
                      target="_blank"
                      rel="nofollow noreferrer"
                      className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-secondary-100 text-secondary-800 text-sm font-semibold hover:bg-secondary-200 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-play-circle-line" aria-hidden="true"></i>
                      Videoyu İzle
                    </a>
                  )}
                  {j.status === "processing" || j.status === "pending" ? (
                    <span className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-accent-100 text-accent-800 text-sm font-medium whitespace-nowrap">
                      <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                      Birkaç dakika sürebilir
                    </span>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Üretim formu */}
      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        title="AI Video Ders Oluştur"
        titleId="ai-video-form-title"
        id="ai-video-form-modal"
        maxWidthClass="max-w-xl"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Başlık</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Örn: 7. Sınıf Matematik: Denklem Kurma"
              className={inputClass}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Ders</label>
              <select
                value={form.subject}
                onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                className={inputClass}
              >
                {SUBJECTS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Avatar Öğretmen</label>
              <select
                value={form.avatar_id}
                onChange={(e) => setForm((f) => ({ ...f, avatar_id: e.target.value }))}
                className={inputClass}
              >
                {options.avatars.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}{a.gender ? ` (${a.gender})` : ""}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Ses</label>
            <select
              value={form.voice_id}
              onChange={(e) => setForm((f) => ({ ...f, voice_id: e.target.value }))}
              className={inputClass}
            >
              {options.voices.map((v) => (
                <option key={v.id} value={v.id}>{v.name}{v.language ? ` · ${v.language}` : ""}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">
              Senaryo (Anlatım Metni)
            </label>
            <textarea
              value={form.script}
              onChange={(e) => setForm((f) => ({ ...f, script: e.target.value }))}
              rows={6}
              placeholder={"Öğretmenin videoda söyleyeceği her şeyi buraya yaz.\n\nÖrn: Merhaba arkadaşlar, bugün denklem kurmayı öğreneceğiz..."}
              className="w-full px-3 py-2.5 rounded-md border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 bg-background-50 resize-y"
            />
            <p className="text-xs text-foreground-400 mt-1.5">
              Video, bu metni avatar öğretmenin seslendirmesiyle oluşturulur. Ne kadar net yazarsan anlatım o kadar iyi olur.
            </p>
          </div>

          {formError && <p className="text-sm text-red-700 bg-red-50 rounded-md px-3 py-2">{formError}</p>}

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="h-10 px-4 rounded-md text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              Vazgeç
            </button>
            <button
              type="submit"
              disabled={busy || !options.configured}
              className="h-10 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-60 transition-colors whitespace-nowrap"
            >
              {busy ? "Başlatılıyor..." : "Videoyu Üret"}
            </button>
          </div>
        </form>
      </Modal>

      <PanelLinks
        links={[
          { label: "Video Dersler", href: "/ogretmen/video-dersler", icon: "ri-play-circle-line" },
          { label: "Derslerim", href: "/ogretmen/derslerim", icon: "ri-book-2-line" },
        ]}
      />
    </div>
  );
}